import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { BoardLettersByNumber, Colors, FigureData, Figures } from "types";
import { ActionCellData } from "services/WebSocketService/types/ActionCellData";
import Figure from "components/Figure/Figure";
import styles from "./Board.module.scss";
import Cell from "./Cell";
import { PromotionDialog } from "../PromotionDialog/PromotionDialog"
import {
	changeFigurePosition,
	removeFigure,
	selectFigures, setGameStarted, setInitialFigures,
} from "redux/gameSlice";
import WebSocketService from "services/WebSocketService/WebSocketService";
import { MoveRequest } from "services/WebSocketService/types/MoveRequest";
import { RequestMoves } from "services/WebSocketService/types/MovesRequest";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Link } from "react-router-dom";
import store from "../../redux/store";
import { ActionRequest } from "services/WebSocketService/types/ActionRequest";
import { PromotionActionRequest } from '../../services/WebSocketService/types/PromotionActionRequest';
import { OnMoveActionRequest } from "services/WebSocketService/types/OnMoveActionRequest";
import { ContextActionData } from "services/WebSocketService/types/ContextActionData";
import { ChoseFigure } from "services/WebSocketService/types/ChoseFigure";
import { PieceInfo } from "services/WebSocketService/types/PieceInfo";
import { supabase, getSession } from "services/Account/Supabase";
import { useSearchParams } from 'react-router-dom';
import { toast, Zoom } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

interface BoardProps {
	choseFigurePos: ChoseFigure | null;
	setChoseFigurePos: (arg0: ChoseFigure | null) => void;
	setTimerState: (data: { timers: Record<string, number>; active_player: string }) => void;
	initPlayers: (data: Array<{ color: 'white' | 'black'; name: string; uid: string; player_time: number }>) => void;
}

interface PieceData {
	color: string;
	position: [number, number]; // x, y coordinates
	move_count: number;
	capture_count: number;
}

interface Piece {
	id: string
	name: string;
	color: string;
	data: PieceData;
}

const Board: React.FC<BoardProps> = ({ choseFigurePos, setChoseFigurePos, setTimerState, initPlayers }) => {


	const dispatch = useAppDispatch();
	const boardRef = useRef<HTMLDivElement>(null);
	const squaresRef = useRef<HTMLUListElement>(null);
	const figures = useAppSelector(selectFigures);
	const [gameWon, setGameWon] = useState<String>("None");
	const gameColorRef = useRef<Colors>(Colors.WHITE);
	const [cellsFigure, setCellsFigure] = useState<{ [key: string]: FigureData | null }>({});
	const [sides, setSides] = useState<{ ally: Colors; enemy: Colors }>({
		ally: gameColorRef.current,
		enemy: gameColorRef.current === Colors.WHITE ? Colors.BLACK : Colors.WHITE,
	});

	const [playerID, setPlayerID] = useState<string>('');
	const [promotionActionData, setPromotionActionData] = useState<ActionCellData | null>(null);

	const [arrows, setArrows] = useState<{ start: [number, number]; end: [number, number] }[]>([]);
	const [drawingArrow, setDrawingArrow] = useState<{ start: [number, number] } | null>(null);
	const [cellEffects, setCellEffects] = useState<{ name: string, position: [number, number], turns_on_board: number, uid: string }[] | null>(null);

	const [lastMove, setLastMove] = useState<{
		from: [number, number] | null;
		to: [number, number] | null;
		affected: [number, number][];
	}>({
		from: null,
		to: null,
		affected: [],
	});

	const fetchUsername = async (uid: string) => {
		const { data, error } = await supabase
			.from("user")
			.select("username")
			.eq("uid", uid)
			.single();

		if (error) {
			console.error("Error fetching username:", error);
			toast.error("Failed to fetch username.");
			return "Guest";
		}

		return data?.username ?? "Guest";
	};

	const invertCoordinate = (coordinate: number) => (gameColorRef.current === Colors.BLACK ? 7 - coordinate : coordinate);

	// Websocket ////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const [searchParams] = useSearchParams();
	const roomId = searchParams.get('room_id');

	const socketUrl = process.env.REACT_APP_SOCKET_URL + '/game' || '';
	const [webSocketService, setWebSocketService] = useState<WebSocketService | null>(null);

	const handleMessage = (message: any) => {

		if (message.type != "timer_update") {
			console.log(message);
		}

		const figures = store.getState().game.figures;

		if (message.type === "init_player") {

			let playerColor;
			if (message.data.color === "white") {
				playerColor = Colors.WHITE
			} else {
				playerColor = Colors.BLACK
			}

			gameColorRef.current = playerColor;
			setSides({
				ally: playerColor,
				enemy: playerColor === Colors.WHITE ? Colors.BLACK : Colors.WHITE,
			})

		} else if (message.type === "game_state") {
			const formatted_pieces: Record<string, any> = {};
			const pieces: Record<string, Piece> = message.data.pieces as Record<string, Piece>; // Ensure correct typing

			for (const [piece_id, piece] of Object.entries(pieces)) {
				const [x, y] = piece.data.position; // Extracting position from `data`

				formatted_pieces[piece_id] = {
					id: piece_id,
					name: piece.name,
					x: x,
					y: y,
					color: piece.color,
				};
			}
			dispatch(setInitialFigures(formatted_pieces));

			// handling square highlighting for board difference (last turn compared to this turn)
			if (message.data.board_diff && message.data.board_diff.length > 0) {
				// Convert board_diff into a list of highlighted positions
				const affectedCells = message.data.board_diff.map(
					([x, y]: [number, number]) => [x, y] as [number, number]
				);

				// Set last move using board_diff
				setLastMove({
					from: affectedCells.length > 0 ? affectedCells[0] : null,
					to: affectedCells.length > 1 ? affectedCells[1] : null, // Highlighting the first two as a default
					affected: affectedCells, // Store all affected cells for additional logic
				});
			} else {
				// Reset lastMove when no board_diff is provided
				setLastMove({ from: null, to: null, affected: [] });
			}

			// if game has been won show banner
			if (message.data.winner && message.data.winner != "None") {
				setGameWon(message.data.winner)
			}
			setChoseFigurePos(null);
			setCellEffects(message.data.cellEffects);

		} else if (message.type === "move") {

			let { pos_from, pos_to } = message.data.move;
			pos_from = [pos_from[0], pos_from[1]];
			pos_to = [pos_to[0], pos_to[1]];

			setLastMove({
				from: pos_from as [number, number],
				to: pos_to as [number, number],
				affected: [pos_from as [number, number], pos_to as [number, number]], // Include the affected cells
			});

			const figure = Object.values(figures).find(
				(fig) => fig.x === pos_from[0] && fig.y === pos_from[1]
			);

			if (figure) {
				moveOrEat(figure, pos_to[0], pos_to[1]);
			} else {
				console.warn(`No figure found at position [${pos_from[0]}, ${pos_from[1]}]`);
			}

			setChoseFigurePos(null);

		} else if (message.type === "request_moves") {

			const move_squares = message.data.move_locations;
			const capture_squares = message.data.capture_locations;
			const action_squares = message.data.action_squares; // squares which, onclick, require additional user input. data format is {type:"promotion", data: {promotion_options: ["knight", ...]}}
			const target_pos = message.data.target;

			// find figure on target square
			const figure = Object.values(figures).find(
				(fig) => fig.x === target_pos[0] && fig.y === target_pos[1]
			);

			if (figure) {

				// Reformat incoming positions to UI format, with coordinates inverted if color is black
				const toPositionMap = (squares: [number, number][], value: boolean) =>
					squares.reduce((acc: Record<string, boolean>, [x, y]: [number, number]) => {
						const displayX = x;
						const displayY = y;
						acc[`${displayX}-${displayY}`] = value;
						return acc;
					}, {});

				// Update chosen figure with inverted positions
				const movePositionMap = toPositionMap(move_squares, true);
				const capturePositionMap = toPositionMap(capture_squares, true);
				const combinedPositionMap = { ...movePositionMap, ...capturePositionMap };

				const actionCells = action_squares.reduce((acc: Record<string, ActionCellData>, action: ActionCellData) => {
					const position = action.data.position;
					const key = `${position[0]}-${position[1]}`;
					acc[key] = action;
					return acc;
				}, {});

				const piece_info: PieceInfo = message.data.target_info
				const context_actions: ContextActionData[] = message.data.context_actions;
				setChoseFigurePos({
					figure,
					availableCells: combinedPositionMap,
					actionCells: actionCells,
					contextActionActive: -1,
					contextActions: context_actions,
					pieceInfo: piece_info
				});
			}

		} else if (message.type === "alert" && message.data) {
			const { type, message: alertMessage } = message.data;

			// Display toast based on the alert type
			if (type === "error") {
				toast.error(alertMessage, {
				});
			} else if (type === "okay") {
				toast.success(alertMessage, {
				});
			} else if (type === "information") {
				toast.info(alertMessage, {
				});
			}
		} else if (message.type === "cell_effects_state") {
			setCellEffects(message.data.cellEffects);
		} else if (message.type == "init_timers") {
			initPlayers(message.data);
		} else if (message.type == "timer_update") {
			setTimerState(message.data);
		}
	};

	const wsInitialized = useRef(false);

	useEffect(() => {

		let wsService: WebSocketService | null = null;

		const initializeWebSocket = async () => {
			const session = await getSession();
			const uid = session?.user?.id;

			if (uid && !webSocketService && !playerID && roomId) {
				if (wsInitialized.current) return;

				const name = session.user.user_metadata?.name || "Guest";

				setPlayerID(uid);

				const fetchedUsername = await fetchUsername(uid);

				wsService = new WebSocketService(roomId, socketUrl, handleMessage, { name: fetchedUsername, player_id: uid });
				setWebSocketService(wsService);

				wsInitialized.current = true;
			}
		}

		initializeWebSocket();

		return () => {
			// Cleanup WebSocket connection when component unmounts
			if (wsService) {
				wsService.closeConnection();
			}
			if (webSocketService) {
				webSocketService.closeConnection();
			}
			setWebSocketService(null);
			wsInitialized.current = false;
		};
	}, [roomId, socketUrl]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			if (webSocketService) {
				webSocketService.closeConnection();
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			if (webSocketService) {
				webSocketService.closeConnection();
			}
		};
	}, [webSocketService]);

	////////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		window.addEventListener("resize", () => { setArrows([]) });
		return () => {
			window.removeEventListener("resize", () => { setArrows([]) });
		};
	}, []);

	const handleMouseDown = (e: React.MouseEvent) => {
		if (e.button === 2) {
			const rect = squaresRef.current?.getBoundingClientRect();
			if (!rect) return;

			const cellSize = rect.width / 8;
			const x = Math.floor((e.clientX - rect.left) / cellSize);
			const y = Math.floor((e.clientY - rect.top) / cellSize);

			const adjustedX = x;
			const adjustedY = y;

			setDrawingArrow({ start: [adjustedX, adjustedY] });
			e.preventDefault();
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (drawingArrow && e.buttons === 2) {
			const rect = squaresRef.current?.getBoundingClientRect();
			if (!rect) return;

			const cellSize = rect.width / 8;
			const x = Math.floor((e.clientX - rect.left) / cellSize);
			const y = Math.floor((e.clientY - rect.top) / cellSize);

			const adjustedX = x;
			const adjustedY = y;

			const end: [number, number] = [adjustedX, adjustedY];
			setArrows((prevArrows) => [
				...prevArrows.filter((a) => a.start !== drawingArrow.start),
				{ start: drawingArrow.start, end },
			]);
		}
	};

	const handleMouseUp = () => {
		if (drawingArrow) {
			setDrawingArrow(null);
		}
	};

	const handleLeftClick = () => {
		setArrows([]);
	};

	const renderArrows = () => {
		if (!squaresRef.current) return null;

		const rect = squaresRef.current.getBoundingClientRect();
		const cellSize = rect.width / 8;

		// Define arrow size dynamically based on cell size or window size
		const strokeWidth = Math.max(6, cellSize * 0.1); // Thicker line width
		const markerWidth = Math.max(10, cellSize * 0.4); // Short arrow length
		const markerHeight = markerWidth * 1.5; // Wider arrow base

		return (
			<svg
				className={styles.arrows}
				style={{
					position: "absolute",
					top: "12px",
					left: "48px",
					width: `calc(100% - ${48 + 12}px)`,
					height: `calc(100% - ${12 + 52 - 5}px)`,
					pointerEvents: "none",
				}}
			>
				{arrows.map((arrow, index) => {
					const [startX, startY] = arrow.start;
					const [endX, endY] = arrow.end;

					// Calculate the center of the start and end cells
					const startCenterX = (startX + 0.5) * cellSize;
					const startCenterY = (startY + 0.5) * cellSize;
					const endCenterX = (endX + 0.5) * cellSize;
					const endCenterY = (endY + 0.5) * cellSize;

					return (
						<line
							key={index}
							x1={startCenterX}
							y1={startCenterY}
							x2={endCenterX}
							y2={endCenterY}
							stroke="#00BFFF"
							strokeWidth={strokeWidth}
							markerEnd="url(#arrowhead)"
						/>
					);
				})}
				<defs>
					<marker
						id="arrowhead"
						markerWidth={markerWidth}
						markerHeight={markerHeight}
						refX={markerWidth - 4}
						refY={markerHeight / 2}
						orient="auto"
						markerUnits="userSpaceOnUse"
						fill="#00BFFF"
					>
						<polygon points={`0 0, ${markerWidth} ${markerHeight / 2}, 0 ${markerHeight}`} />
					</marker>
				</defs>
			</svg>
		);
	};

	const isAvailableCellForMove = (x: number, y: number): boolean => {
		if (choseFigurePos && choseFigurePos.availableCells[`${x}-${y}`]) {
			return true;
		}
		return false;
	}

	const isCellHavingFigure = (x: number, y: number): boolean => {
		return cellsFigure[`${x}-${y}`] ? true : false;
	}

	const moveOn = (figure: FigureData, x: number, y: number) => {
		cellsFigure[`${figure.x}-${figure.y}`] = null;
		cellsFigure[`${x}-${y}`] = figure;
		dispatch(changeFigurePosition({ figure, x, y }));
		setChoseFigurePos(null);
	}

	const cellClicked = (x: number, y: number, actionData: ActionCellData | null): void => {

		if (!choseFigurePos) return;
		if (sides.ally !== choseFigurePos.figure.color) return;

		if (actionData) { // if clicked cell had an associated action

			console.log("ACTIONCELL CLICKED")
			console.log(actionData)

			if (actionData.type === "onmove_choice_action" && actionData.data.promotion_options) {
				setPromotionActionData(actionData);
			} else if (actionData.type === "onmove_action") {
				if (webSocketService && roomId) {
					const actionMessage: OnMoveActionRequest = {
						type: "onmove_action",
						room_id: roomId,
						player_id: playerID,
						action: actionData,
						data: {}
					};
					webSocketService.sendMessage(actionMessage);
					setChoseFigurePos(null);
				}
			} else if (actionData.type === "action") {
				if (webSocketService && roomId) {
					actionData['data']['position'] = [x, y];
					const actionMessage: ActionRequest = {
						type: "action",
						room_id: roomId,
						player_id: playerID,
						action: actionData,
						data: {}
					};
					webSocketService.sendMessage(actionMessage);
					setChoseFigurePos(null);
				}
			}
		} else {
			if (!choseFigurePos) return;
			if (!choseFigurePos.availableCells[`${x}-${y}`]) return;
			if (webSocketService && roomId) {
				const moveMessage: MoveRequest = {
					type: "move",
					room_id: roomId,
					player_id: playerID,
					data: {
						move: {
							player_color: gameColorRef.current,
							pos_from: [choseFigurePos.figure.x, choseFigurePos.figure.y],
							pos_to: [x, y],
						},
					},
				};
				webSocketService.sendMessage(moveMessage);
			}
		}
	};

	const isActionCell = (x: number, y: number): boolean => {
		if (choseFigurePos && choseFigurePos?.actionCells && choseFigurePos.actionCells[`${x}-${y}`]) {
			let actionData = choseFigurePos && choseFigurePos?.actionCells[`${x}-${y}`];
			return Boolean(actionData);
		}
		return false;
	}

	const hasEffectCell = (x: number, y: number): string => {
		if (!cellEffects) return "none";
		const effect = cellEffects.find(effect => effect.position[0] === x && effect.position[1] === y);
		return effect ? effect.name : "none";
	}

	const isLastMoveCell = (x: number, y: number): boolean => {
		// checks if x, y is in lastmove affected
		if (lastMove.affected.length > 0) {
			return lastMove.affected.some(([ax, ay]) => ax === x && ay === y);
		}
		return false;
	};

	const isContextActionCell = (x: number, y: number): boolean => {
		if (choseFigurePos && choseFigurePos.contextActionActive !== -1) {
			const contextActionData = choseFigurePos.contextActions[choseFigurePos.contextActionActive];
			return contextActionData.context_action_squares.some(([cx, cy]) => cx === x && cy === y);
		}
		return false;
	};

	const initCells = (): JSX.Element[] => {

		const cells: JSX.Element[] = [];

		for (let y = 7; y >= 0; y--) {
			for (let x = 0; x <= 7; x++) {
				const displayX = invertCoordinate(x);
				const displayY = invertCoordinate(y);
				cellsFigure[`${displayX}-${displayY}`] = null;
				const boardLetter = BoardLettersByNumber[displayX];
				const cellColor = (displayX + displayY) % 2 === 0 ? Colors.BLACK : Colors.WHITE;

				let actionData: ActionCellData | null = choseFigurePos && choseFigurePos?.actionCells && choseFigurePos.actionCells[`${displayX}-${displayY}`] ? choseFigurePos.actionCells[`${displayX}-${displayY}`] : null;
				let contextActionData: ContextActionData | null = choseFigurePos && choseFigurePos.contextActionActive !== -1 ? choseFigurePos.contextActions[choseFigurePos.contextActionActive] : null;
				let isMoveCell: boolean = isAvailableCellForMove(displayX, displayY);

				if (contextActionData) { // if there's an active context action, show only the context action squares.

					if (isContextActionCell(invertCoordinate(x), invertCoordinate(y))) {
						actionData = contextActionData.action
					} else {
						actionData = null;
					}

					isMoveCell = false;
				}

				cells.push(
					<Cell
						color={cellColor}
						x={boardLetter}
						y={displayY}
						key={`${boardLetter}-${displayY}`}
						isAvailableForMove={isMoveCell}
						isHavingFigure={isCellHavingFigure(displayX, displayY)}
						cellClicked={() => cellClicked(displayX, displayY, actionData)}
						isSelected={isSelectedCell(displayX, displayY)}
						actionData={actionData}
						isLastMove={isLastMoveCell(displayX, displayY)}
						hasEffectCell={hasEffectCell(displayX, displayY)}
					/>
				);
			}
		}
		return cells;
	};

	const isEatableFigure = (figure: FigureData): boolean => {
		if (!choseFigurePos) return false;
		return choseFigurePos.availableCells[`${figure.x}-${figure.y}`];
	}

	const isSelectedFigure = (figure: FigureData): boolean => {
		if (!choseFigurePos) return false;
		return choseFigurePos.figure.id === figure.id;
	}

	const isSelectedCell = (x: number, y: number): boolean => {
		if (!choseFigurePos) return false;
		return choseFigurePos.figure.x === x && choseFigurePos.figure.y === y;
	}

	const initFigures = (): JSX.Element[] => {
		const figuresJSX: JSX.Element[] = [];

		for (let item in figures) {
			if (!figures[item].id || !figures[item].color) continue;
			const displayX = invertCoordinate(figures[item].x);
			const displayY = invertCoordinate(figures[item].y);
			cellsFigure[`${displayX}-${displayY}`] = figures[item];

			let actionData: ActionCellData | null = choseFigurePos && choseFigurePos?.actionCells && choseFigurePos.actionCells[`${displayX}-${displayY}`] ? choseFigurePos.actionCells[`${displayX}-${displayY}`] : null;
			let contextActionData: ContextActionData | null = choseFigurePos && choseFigurePos.contextActionActive !== -1 ? choseFigurePos.contextActions[choseFigurePos.contextActionActive] : null;

			if (contextActionData) {
				if (isContextActionCell(displayX, displayY)) {
					actionData = contextActionData.action
				} else {
					actionData = null;
				}
			}

			figuresJSX.push(
				<Figure
					figureClicked={figureClicked}
					key={figures[item].id}
					figure={{ ...figures[item], x: displayX, y: displayY }}
					isEatable={isEatableFigure(figures[item])}
					isSelected={isSelectedFigure(figures[item])}
					relativePos={false}
				/>
			);
		}

		return figuresJSX;
	};

	const figureClicked = (figure: FigureData) => {

		if (choseFigurePos && (sides.ally === choseFigurePos.figure.color)) {
			// if piece is currently selected (implies that there may be action cells)
			let { x, y } = figure;
			x = invertCoordinate(x); y = invertCoordinate(y);
			if (isContextActionCell(x, y)) {
				const contextActionData = choseFigurePos.contextActions[choseFigurePos.contextActionActive];
				let action = contextActionData.action;
				action.data['position'] = [x, y]

				if (webSocketService && roomId) {
					const actionMessage: ActionRequest = {
						type: "action",
						room_id: roomId,
						player_id: playerID,
						action: action,
						data: {}
					};
					webSocketService.sendMessage(actionMessage);
					setChoseFigurePos(null);
				}
				return;
			} else if (isActionCell(x, y)) {
				const actionData: ActionCellData = choseFigurePos.actionCells[`${x}-${y}`]
				if (actionData.type === "onmove_choice_action" && actionData.data.promotion_options) {
					setPromotionActionData(actionData);
					return;
				} else if (actionData.type === "onmove_action") {
					if (webSocketService && roomId) {
						const actionMessage: OnMoveActionRequest = {
							type: "onmove_action",
							room_id: roomId,
							player_id: playerID,
							action: actionData,
							data: {}
						};
						webSocketService.sendMessage(actionMessage);
						setChoseFigurePos(null);
					}
				}
			}

			if (choseFigurePos.availableCells[`${invertCoordinate(figure.x)}-${invertCoordinate(figure.y)}`] && choseFigurePos.figure.color !== figure.color) {
				// if the clicked piece is within availablecels for movement AND isn't the same piece as the currently selected piece and is of enemy color

				if (webSocketService && roomId) {
					const moveMessage: MoveRequest = {
						type: "move",
						room_id: roomId,
						player_id: playerID,
						data: {
							move: {
								player_color: gameColorRef.current,
								pos_from: [choseFigurePos.figure.x, choseFigurePos.figure.y],
								pos_to: [invertCoordinate(figure.x), invertCoordinate(figure.y)],
							},
						},
					};
					webSocketService.sendMessage(moveMessage);
				}

				return;
			}

		}

		if (choseFigurePos) {
			if (choseFigurePos.figure.name === figure.name && invertCoordinate(figure.x) === choseFigurePos.figure.x && choseFigurePos.figure.y === invertCoordinate(figure.y) && choseFigurePos.figure.color === figure.color) {
				// if clicked piece is the currently selected piece
				setChoseFigurePos(null);
				return;
			}
		}

		// if (sides.ally !== figure.color) return;

		// clicked a new piece of the player's color. request moves from the server

		if (webSocketService && roomId) {
			const requestMovesMessage: RequestMoves = {
				type: "request_moves",
				room_id: roomId,
				player_id: playerID,
				data: {
					target: [invertCoordinate(figure.x), invertCoordinate(figure.y)],
				},
			};
			webSocketService.sendMessage(requestMovesMessage);
		}
	}

	const eatFigure = (figure: FigureData): void => {
		cellsFigure[`${figure.x}-${figure.y}`] = null;
		dispatch(removeFigure(figure));
	}

	const moveOrEat = (figure: FigureData, x: number, y: number): void => {
		const figureOnCell = cellsFigure[`${invertCoordinate(x)}-${invertCoordinate(y)}`]; // have to invert just this fetch due to cellsFigure key being white coordinate
		if (figureOnCell && figureOnCell.color !== figure.color) eatFigure(figureOnCell);
		moveOn(figure, x, y);
	}

	const getGameWonJSX = (): JSX.Element | null => {
		if (gameWon == "None") return null;
		const color = gameWon.toUpperCase();

		return <div className={styles.gameWon}>
			<h2 className={styles.gameWonTitle}>{color} won</h2>
			<Link to="/home" className={styles.gameWonButton}>Main page</Link>
		</div>;
	}

	useEffect(() => {
		dispatch(setGameStarted(true));
	}, [])

	useEffect(() => {
		setSides({
			ally: gameColorRef.current,
			enemy: gameColorRef.current === Colors.WHITE ? Colors.BLACK : Colors.WHITE,
		});
	}, [gameColorRef]);

	const handlePromotionSelect = (piece: string, actionData: ActionCellData) => {

		// Trigger the move with selected promotion piece
		if (choseFigurePos) {

			if (webSocketService && roomId) {
				const actionMessage: PromotionActionRequest = {
					type: "onmove_action",
					room_id: roomId,
					player_id: playerID,
					action: actionData,
					data: {
						promotion_choice: piece
					}
				};
				console.log(actionMessage)
				webSocketService.sendMessage(actionMessage);
			}
		}
		setPromotionActionData(null); // Close the dialog after selection
		setChoseFigurePos(null);
	};

	return <div
		className={styles.boardWrapper}
		ref={boardRef}
	>

		{/* Left side numbering */}
		<ul className={styles.boardLeft}>
			{(gameColorRef.current === Colors.BLACK ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8]).map((number) => (
				<li className={styles.boardLeftItem} key={`left-${number}`}>{number}</li>
			))}
		</ul>

		{/* Bottom side lettering */}
		<ul className={styles.boardBottom}>
			{(gameColorRef.current === Colors.BLACK ? ['H', 'G', 'F', 'E', 'D', 'C', 'B', 'A'] : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']).map((letter) => (
				<li className={styles.boardBottomItem} key={`bottom-${letter}`}>{letter}</li>
			))}
		</ul>

		{/* Board Cells and Figures */}
		<ul className={styles.board} ref={squaresRef}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onClick={handleLeftClick}
			onContextMenu={(e) => e.preventDefault()}>
			{initCells()}
			{initFigures()}
		</ul>

		{/* Promotion Dialog - choosing piece on promotion */}
		{promotionActionData && (
			<PromotionDialog
				actionData={promotionActionData}
				onSelect={handlePromotionSelect}
				onClose={() => setPromotionActionData(null)}
			/>
		)}

		{getGameWonJSX()}
		{renderArrows()}
	</div>
}

export default Board;